import React, {useEffect, useMemo, useRef} from 'react';
import {Platform} from 'react-native';
import {useIsFocused} from '@react-navigation/native';

// import {tabs} from '../constants';
import {screens} from '../screens';
import {useAppDispatch, useAppSelector} from '../hooks';
import {APP_CONFIG} from '../config';
import {setScreen} from '../store/slices/tabSlice';

const BRAND_NAME = 'Level Delivery';

type ScreenMeta = {
  path: string;
  title: string;
};

const createDocumentTitle = (label: string) => `${label} | ${BRAND_NAME}`;

const normalizePath = (path: string) => {
  if (!path || path === '/') {
    return '/';
  }

  let normalized = path;

  while (normalized.length > 1 && normalized.endsWith('/')) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
};

const findScreenByPath = (
  meta: Record<string, ScreenMeta>,
  path: string,
): [string, ScreenMeta] | undefined => {
  const normalizedPath = normalizePath(path);
  return Object.entries(meta).find(([, value]) => value.path === normalizedPath);
};

const TabNavigator: React.FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const currentTabScreen = useAppSelector((state) => state.tab.screen);
  const notificationsEnabled = APP_CONFIG.ui.notificationsEnabled ?? false;
  const isFocused = useIsFocused();
  const isWeb = Platform.OS === 'web';
  const hasSyncedRef = useRef(false);
  const isHandlingPopRef = useRef(false);

  const screenMeta = useMemo<Record<string, ScreenMeta>>(() => {
    const meta: Record<string, ScreenMeta> = {
      Home: {path: '/', title: createDocumentTitle('Accueil')},
      Menu: {path: '/menu', title: createDocumentTitle('Menu')},
      Order: {path: '/order', title: createDocumentTitle('Commande')},
      Favorite: {path: '/favorite', title: createDocumentTitle('Favoris')},
    };

    if (notificationsEnabled) {
      meta.Notification = {
        path: '/notifications',
        title: createDocumentTitle('Notifications'),
      };
    }

    return meta;
  }, [notificationsEnabled]);

  useEffect(() => {
    if (!notificationsEnabled && currentTabScreen === 'Notification') {
      dispatch(setScreen('Home'));
    }
  }, [notificationsEnabled, currentTabScreen, dispatch]);

  useEffect(() => {
    if (!isWeb || !isFocused || hasSyncedRef.current) {
      return;
    }

    const globalWindow = typeof window !== 'undefined' ? window : undefined;

    if (!globalWindow) {
      return;
    }

    const match = findScreenByPath(screenMeta, globalWindow.location.pathname);
    const fallbackScreen = match?.[0] ?? 'Home';
    const fallbackMeta = screenMeta[fallbackScreen] ?? screenMeta.Home;

    if (fallbackScreen !== currentTabScreen) {
      dispatch(setScreen(fallbackScreen));
    }

    globalWindow.history.replaceState({screen: fallbackScreen}, '', fallbackMeta.path);
    globalWindow.document.title = fallbackMeta.title;
    hasSyncedRef.current = true;
  }, [
    currentTabScreen,
    dispatch,
    isFocused,
    isWeb,
    screenMeta,
  ]);

  useEffect(() => {
    if (!isWeb || !isFocused) {
      return;
    }

    const globalWindow = typeof window !== 'undefined' ? window : undefined;

    if (!globalWindow) {
      return;
    }

    const handlePopState = () => {
      if (!hasSyncedRef.current) {
        return;
      }

      const match = findScreenByPath(screenMeta, globalWindow.location.pathname);

      if (!match) {
        return;
      }

      const [nextScreen, meta] = match;

      if (nextScreen !== currentTabScreen) {
        isHandlingPopRef.current = true;
        dispatch(setScreen(nextScreen));
      }

      globalWindow.document.title = meta.title;
    };

    globalWindow.addEventListener('popstate', handlePopState);

    return () => {
      globalWindow.removeEventListener('popstate', handlePopState);
    };
  }, [currentTabScreen, dispatch, isFocused, isWeb, screenMeta]);

  const activeScreen =
    !notificationsEnabled && currentTabScreen === 'Notification'
      ? 'Home'
      : currentTabScreen;

  useEffect(() => {
    if (!isWeb || !isFocused || !hasSyncedRef.current) {
      return;
    }

    const globalWindow = typeof window !== 'undefined' ? window : undefined;

    if (!globalWindow) {
      return;
    }

    const meta = screenMeta[activeScreen] ?? screenMeta.Home;
    const currentState = globalWindow.history.state as {screen?: string} | null;
    const currentPath = normalizePath(globalWindow.location.pathname);

    globalWindow.document.title = meta.title;

    if (isHandlingPopRef.current) {
      isHandlingPopRef.current = false;

      if (currentPath !== meta.path || currentState?.screen !== activeScreen) {
        globalWindow.history.replaceState(
          {screen: activeScreen},
          '',
          meta.path,
        );
      }

      return;
    }

    if (currentPath === meta.path && currentState?.screen === activeScreen) {
      return;
    }

    globalWindow.history.pushState({screen: activeScreen}, '', meta.path);
  }, [activeScreen, isFocused, isWeb, screenMeta]);

  const renderScreen = () => {
    return (
      <React.Fragment>
        {activeScreen === 'Home' && <screens.Home />}
        {activeScreen === 'Menu' && <screens.Menu />}
        {activeScreen === 'Order' && <screens.Order />}
        {activeScreen === 'Favorite' && <screens.Favorite />}
        {notificationsEnabled && activeScreen === 'Notification' && (
          <screens.Notification />
        )}
      </React.Fragment>
    );
  };

  return renderScreen();
};

export default TabNavigator;
